import 'moment/locale/da';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/nl';
import 'moment/locale/pt';
import 'moment/locale/ru';

import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  SocialAuthServiceConfig,
  SocialLoginModule,
} from '@abacritt/angularx-social-login';
import { registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import localeItExtra from '@angular/common/locales/extra/it';
import localeIt from '@angular/common/locales/it';
import { ErrorHandler, Injectable, NgModule } from '@angular/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { TranslateDefaultParser, TranslateLoader, TranslateModule, TranslateParser } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import * as moment from 'moment';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { environment } from 'src/environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LocalizationService } from './shared/services/localization.service';
import { SharedModule } from './shared/shared.module';

Bugsnag.start({
  apiKey: 'de2afd053e3b4877f477c5157cb7e3a0',
  releaseStage: environment.environmentName == 'prod' ? 'production' : environment.environmentName == 'waf' ? 'staging' : environment.environmentName == 'localhost' ? 'development' : null
});

export function errorHandlerFactory() {
  return new BugsnagErrorHandler()
}

export function momentAdapterFactory() {
  return adapterFactory(moment);
};

registerLocaleData(localeIt, localeItExtra);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@Injectable()
export class TranslateCustomParser extends TranslateDefaultParser {
  getValue(target: any, key: string): any {
    if (!!key && key.startsWith('shared.')) {
      key = key.replace('shared.', 'interfaces.');
    }

    let res = super.getValue(target, key);

    if (typeof res === 'object') {
      return key;
    } else {
      return res;
    }
  }
}

export class PaginatorIntl extends MatPaginatorIntl {

  public localizationService: LocalizationService;
  public itemsPerPageLabel: string;
  public nextPageLabel: string;
  public previousPageLabel: string;
  public firstPageLabel: string;
  public lastPageLabel: string;

  getRangeLabel = (page: number, pageSize: number, length: number) => {
    const of = this.localizationService ? this.localizationService.getTranslationSync('globals.paginator.of') : 'of';

    if (length == 0 || pageSize == 0) {
      return '0 ' + of + ' ' + length;
    }

    length = Math.max(length, 0);

    const startIndex = page * pageSize;

    const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;

    return startIndex + 1 + ' - ' + endIndex + ' ' + of + ' ' + length;
  };

  injectLocalizationService(s: LocalizationService) {
    this.localizationService = s;

    this.localizationService.languageChanged.subscribe(() => {
      this.translateLabels();
    });

    this.translateLabels();
  }

  translateLabels() {
    this.firstPageLabel = this.localizationService.getTranslationSync('globals.paginator.firstPage');
    this.lastPageLabel = this.localizationService.getTranslationSync('globals.paginator.lastPage');
    this.itemsPerPageLabel = this.localizationService.getTranslationSync('globals.paginator.itemsPerPage');
    this.nextPageLabel = this.localizationService.getTranslationSync('globals.paginator.nextPage');
    this.previousPageLabel = this.localizationService.getTranslationSync('globals.paginator.previousPage');

    this.changes.next();
  }
}

const cookieConfig: NgcCookieConsentConfig = {
  position: 'bottom',
  theme: 'edgeless',
  palette: {
    popup: {
      background: '#2d3e50',
      text: '#ffffff',
      link: '#ffffff'
    },
    button: {
      background: '#006dc9',
      text: '#ffffff',
      border: '#006dc9'
    }
  },
  type: 'opt-out',
  enabled: false
};

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    SharedModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MatMomentDateModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      parser: {
        provide: TranslateParser,
        useClass: TranslateCustomParser,
      }
    }),
    CalendarModule.forRoot({ provide: DateAdapter, useFactory: momentAdapterFactory }),
    SocialLoginModule,
    NgcCookieConsentModule.forRoot(cookieConfig)
  ],
  exports: [],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    {
      provide: MatPaginatorIntl,
      useFactory: (translate) => {
        const service = new PaginatorIntl();
        service.injectLocalizationService(translate);
        return service;
      },
      deps: [LocalizationService]
    },
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider('279440191228-80pd715g6gue94d34n0bbeg40vlaem59.apps.googleusercontent.com')
          },
          {
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider('1670232956600280')
            //provider: new FacebookLoginProvider('309812297049359')
          }
        ],
      } as SocialAuthServiceConfig
    },
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    { provide: MAT_PAGINATOR_DEFAULT_OPTIONS, useValue: { formFieldAppearance: 'fill' } },
    { provide: MAT_CARD_CONFIG, useValue: { appearance: 'outlined' } },
    { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
    { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true, panelWidth: null } },
    { provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: true } }
  ],
  bootstrap: [AppComponent]
})

export class AppModule { }